/**
 * The page shown to someone signed in on the wrong host (D39): a staff account on the customer app ("use the staff
 * console"), or a customer's account on the staff console ("use ACP"). The API refuses each of them anyway — the staff
 * tenant every product write, a customer every console route — so this says so plainly instead of a broken page, with
 * the right host's address when it is known and a way to sign out.
 */
import { ExternalLinkIcon, LogOutIcon, ShieldAlertIcon } from 'lucide-react';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card.js';

export type WrongHostProps = {
  /** `staff-on-app`: a staff account on the customer app; `customer-on-console`: anyone else on the console. */
  readonly mode: 'staff-on-app' | 'customer-on-console';
  readonly email: string | null;
  readonly tenantId: string | null;
  /** The right host's origin, or null when it cannot be derived (a local stack). */
  readonly target: string | null;
  readonly onSignOut: () => void;
};

const TEXT = {
  'staff-on-app': {
    title: 'Staff accounts use the console',
    description: 'You are signed in with an ACP staff account. Staff work in the staff console, not in the customer app.',
    body: 'Studios, rundowns and productions are not run from the staff tenant. To demo or try the product, use your ordinary account in a demo tenant (created from the console).',
    link: 'Open the staff console',
  },
  'customer-on-console': {
    title: 'This is ACP’s staff console',
    description: 'Only ACP staff can use this site.',
    body: 'Your account belongs to an organisation using ACP. Sign in to ACP itself to reach your rundowns and productions.',
    link: 'Open ACP',
  },
} as const;

export function WrongHost({ mode, email, tenantId, target, onSignOut }: WrongHostProps) {
  const text = TEXT[mode];
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 text-foreground" data-testid="wrong-host" data-mode={mode}>
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlertIcon className="size-5 text-warning" aria-hidden="true" />
            {text.title}
          </CardTitle>
          <CardDescription>{text.description}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm">
          <p>{text.body}</p>
          <p className="text-muted-foreground" data-testid="wrong-host-account">
            Signed in as {email ?? 'unknown'}
            {tenantId === null ? '' : ` (${tenantId})`}.
          </p>
        </CardContent>
        <CardFooter className="flex flex-wrap gap-2">
          {target === null ? null : (
            <Button asChild data-testid="wrong-host-link">
              <a href={target}>
                <ExternalLinkIcon />
                {text.link}
              </a>
            </Button>
          )}
          <Button variant="outline" onClick={onSignOut} data-testid="wrong-host-sign-out">
            <LogOutIcon />
            Sign out
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
